import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
  type MessagePayload,
} from "firebase/messaging";
import { readFirebaseConfig, type FirebaseWebConfig } from "./firebaseConfig";

// Scoped away from "/" so this never displaces MSW's mock worker, which owns
// the default scope in dev (see web/src/mocks/browser.ts).
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";

let cachedApp: FirebaseApp | null = null;
let cachedMessaging: Messaging | null = null;

function appFor(config: FirebaseWebConfig): FirebaseApp {
  cachedApp ??= initializeApp({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
  });
  return cachedApp;
}

function messagingFor(config: FirebaseWebConfig): Messaging {
  cachedMessaging ??= getMessaging(appFor(config));
  return cachedMessaging;
}

/**
 * Config present, secure context, and the browser APIs FCM needs. Checked
 * before every entry point below so an unconfigured or unsupported browser is
 * a clean no-op rather than a thrown error.
 */
export async function isPushSupported(): Promise<boolean> {
  if (readFirebaseConfig() === null) return false;
  if (!isSecureContext) return false;
  if (!("serviceWorker" in navigator) || !("Notification" in window)) {
    return false;
  }
  return isSupported();
}

async function registerMessagingServiceWorker(
  config: FirebaseWebConfig,
): Promise<ServiceWorkerRegistration> {
  const query = new URLSearchParams({
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    projectId: config.projectId,
    messagingSenderId: config.messagingSenderId,
    appId: config.appId,
  });
  return navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${query.toString()}`,
    { scope: SW_SCOPE },
  );
}

/**
 * Requests notification permission and returns an FCM token. Must be called
 * from an explicit user gesture -- browsers penalise permission prompts fired
 * on page load, and an unprompted request here would fire on every
 * authenticated visit before the user has ever opted in.
 */
export async function requestPermissionAndToken(): Promise<string | null> {
  const config = readFirebaseConfig();
  if (config === null || !(await isPushSupported())) return null;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  const registration = await registerMessagingServiceWorker(config);
  return getToken(messagingFor(config), {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: registration,
  });
}

/**
 * Silently re-derives the current token when permission was already granted
 * on a previous visit -- no prompt, safe to call on every authenticated page
 * load.
 */
export async function currentTokenIfGranted(): Promise<string | null> {
  const config = readFirebaseConfig();
  if (
    config === null ||
    !(await isPushSupported()) ||
    Notification.permission !== "granted"
  ) {
    return null;
  }

  const registration = await registerMessagingServiceWorker(config);
  return getToken(messagingFor(config), {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: registration,
  });
}

export function onForegroundMessage(
  handler: (payload: MessagePayload) => void,
): () => void {
  const config = readFirebaseConfig();
  if (config === null) return () => {};
  return onMessage(messagingFor(config), handler);
}

export async function deleteCurrentToken(): Promise<void> {
  const config = readFirebaseConfig();
  if (config === null || cachedMessaging === null) return;
  await deleteToken(cachedMessaging);
}
